<script setup lang="ts">
import { api } from '@/api';
import { MedicalSystemCatalogDto, MedicalSystemRtSystemDto } from '@/api/generator';
import { type Scope } from '@/views/enumerations/types';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import { buildOdataQuery } from '@/utils/odata';
import FormAddButton from '@/views/tasks/components/FormAddButton.vue';
import { mdiPlus } from '@mdi/js';
import { useField } from 'vee-validate';
import { onMounted, ref, toRef, useTemplateRef } from 'vue';
import JobConfigurationFormTasks from './JobConfigurationFormTasks.vue';
import JobConfigurationTargetSelection from './JobConfigurationTargetSelection.vue';

const scope = ref<Scope>(); // needs to be defined in Dto

const medicalSystems = ref<MedicalSystemRtSystemDto[]>([]);
const medicalSystemCatalogItems = ref<MedicalSystemCatalogDto[]>([]);

const mode = useFormMode();

async function getSystemEntities() {
  const { result } = await api.medicalSystemsApi.getMedicalSystemRtSystemList({
    ...buildOdataQuery({
      filter: {
        category: scope.value?.rtSystemCategory,
        subCategory: scope.value?.rtSystemSubCategory,
      },
    }),
  });

  const { result: catalogItems } = await api.medicalSystemsApi.getAllMedicalSystemsCatalog();

  medicalSystems.value = result?.value || [];
  medicalSystemCatalogItems.value = catalogItems || [];
}

const { value: targetSystem, setValue: setTargetSystem } = useField<
  MedicalSystemCatalogDto | undefined
>(toRef(() => `targetSystem`));

function update(targetSystemId: string | undefined) {
  setTargetSystem(medicalSystemCatalogItems.value.find((item) => item.id === targetSystemId));
}

onMounted(() => {
  getSystemEntities();
});

const jobConfigurationSelectionRef = useTemplateRef<
  InstanceType<typeof JobConfigurationTargetSelection>
>('jobConfigurationSelection');
</script>

<template>
  <div class="d-flex flex-column ga-6">
    <v-card class="container-level-1">
      <v-card-item>
        <v-card-title>
          <h5>{{ $t('MedicalSystemEntity_one') }}</h5>

          <VSpacer />
          <FormAddButton
            v-if="mode !== 'details'"
            :prependIcon="mdiPlus"
            :label="'SelectMedicalSystem'"
            @click="jobConfigurationSelectionRef?.toggleModal()"
          />
        </v-card-title>
      </v-card-item>

      <v-card-text>
        <JobConfigurationTargetSelection
          ref="jobConfigurationSelection"
          :options="medicalSystems"
          :value="targetSystem?.id"
          @update="update"
        />
      </v-card-text>
    </v-card>

    <JobConfigurationFormTasks name="tasks" :targetSystemEntityId="targetSystem?.id" />
  </div>
</template>
